import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useIsLandscape } from '../../hooks/useIsLandscape';
import { PickerMenu } from '../adjustments/PickerMenu';
import {
  ADJUSTMENT_SPECS,
  FILTER_CATEGORY_LABELS,
  FILTER_MENU,
  type AdjustmentKind,
  type FilterCategory,
  type FilterMenuItem,
} from '../adjustments/registry';

interface FilterPickerProps {
  /** Called when the user picks a filter kind with an implemented
   *  spec. Picker closes itself before invoking. */
  onPick: (kind: AdjustmentKind) => void;
  /** When true, the trigger renders disabled and clicking does
   *  nothing. Mirrors AdjustmentsPicker — filters need a
   *  rasterizable target same as adjustments do. */
  disabled?: boolean;
}

/** Filter… picker — mirrors `AdjustmentsPicker` against
 *  `FILTER_MENU` instead of `ADJUSTMENT_MENU`. The split is purely
 *  UI: under the hood both pickers route into the same modal and
 *  produce the same `LayerNode.type === 'adjustment'` config. */
export function FilterPicker({ onPick, disabled = false }: FilterPickerProps) {
  const [open, setOpen] = useState(false);
  const isLandscape = useIsLandscape();

  const grouped = useMemo(() => {
    const out = new Map<FilterCategory, FilterMenuItem[]>();
    for (const item of FILTER_MENU) {
      const list = out.get(item.category) ?? [];
      list.push(item);
      out.set(item.category, list);
    }
    return out;
  }, []);

  const handlePick = (item: FilterMenuItem) => {
    if (!ADJUSTMENT_SPECS[item.kind]) return; // unimplemented — ignore click
    setOpen(false);
    onPick(item.kind);
  };

  const enabledClass =
    'w-full px-3 py-1.5 text-xs rounded-md border border-border text-foreground hover:bg-accent';
  const disabledClass =
    'w-full px-3 py-1.5 text-xs rounded-md border border-border text-muted-foreground/50 cursor-not-allowed bg-muted/30';
  const triggerClass = disabled ? disabledClass : enabledClass;
  const disabledTitle = 'Select a layer with pixels (image, paint, or a group containing one)';

  if (disabled) {
    return (
      <button type="button" className={triggerClass} disabled title={disabledTitle}>
        Filter&hellip;
      </button>
    );
  }

  if (isLandscape) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger className={triggerClass}>Filter&hellip;</PopoverTrigger>
        <PopoverContent
          align="end"
          // Radix's `--radix-popover-content-available-height` is the
          // actual space left between the trigger and the viewport
          // edge after collision detection — using it (instead of a
          // raw `80vh`) makes the picker scroll to fit whatever space
          // is genuinely available rather than getting clipped when
          // the trigger sits low in the viewport.
          className="w-64 p-2 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto"
        >
          <PickerMenu
            grouped={grouped}
            categoryLabels={FILTER_CATEGORY_LABELS}
            onPick={handlePick}
          />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      <button type="button" className={triggerClass} onClick={() => setOpen(true)}>
        Filter&hellip;
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-lg max-h-[80vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Filter</SheetTitle>
          </SheetHeader>
          <div className="pt-2">
            <PickerMenu
              grouped={grouped}
              categoryLabels={FILTER_CATEGORY_LABELS}
              onPick={handlePick}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
